// ─── "New chat" — pick a colleague (DM) or several (group) ────────────────────
// See CHAT_E2E_DESIGN.md §8. Reuses the company member list already loaded by
// CRMContext (subscribeUsers) rather than re-subscribing — every page that
// needs "everyone in my company" pulls from the same `users` array.
//
// Selecting exactly one person creates/reopens a DM immediately. Selecting
// two or more switches to "group" mode and asks for a group name before
// creating. This mirrors how Teams/Slack collapse "start a chat" and "start
// a group" into one picker rather than two separate flows.

import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

export default function NewChatModal({ users, currentUserId, onCreateDm, onCreateGroup, onClose }) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (users || [])
      .filter(u => u.id !== currentUserId)
      .filter(u => !term || (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term));
  }, [users, currentUserId, search]);

  const toggle = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
    setError('');
  };

  const isGroup = selectedIds.length > 1;

  const handleCreate = async () => {
    if (selectedIds.length === 0 || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      if (isGroup) {
        await onCreateGroup(selectedIds, groupName);
      } else {
        await onCreateDm(selectedIds[0]);
      }
    } catch (err) {
      console.error('[NewChatModal]', err);
      setError(err.message || 'Could not start the chat. Please try again.');
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 420, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>New chat</h2>
          <button onClick={onClose} className="btn-ghost" style={{ padding: 6, width: 30, height: 30, borderRadius: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <input
          className="crm-input"
          placeholder="Search people..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 10, flexShrink: 0 }}
          autoFocus
        />

        {selectedIds.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, flexShrink: 0 }}>
            {selectedIds.map(id => {
              const u = (users || []).find(x => x.id === id);
              if (!u) return null;
              return (
                <span key={id} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 4px 10px',
                  borderRadius: 999, background: 'var(--color-primary-soft)', color: 'var(--color-primary)',
                  fontSize: 12, fontWeight: 600,
                }}>
                  {u.name || u.email}
                  <span onClick={() => toggle(id)} style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>×</span>
                </span>
              );
            })}
          </div>
        )}

        {isGroup && (
          <input
            className="crm-input"
            placeholder="Group name"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            style={{ marginBottom: 10, flexShrink: 0 }}
          />
        )}

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 120, border: '1px solid var(--border)', borderRadius: 10 }}>
          {candidates.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 12.5, color: 'var(--text-muted)' }}>No matching teammates.</div>
          )}
          {candidates.map(u => {
            const checked = selectedIds.includes(u.id);
            const initials = (u.name || u.email || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
            return (
              <div
                key={u.id}
                onClick={() => toggle(u.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
                  background: checked ? 'var(--color-primary-soft)' : 'transparent',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                }}
              >
                <div className="avatar" style={{ background: u.avatar?.bg || '#7c3aed', color: u.avatar?.color || '#fff', width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name || u.email}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.role}</div>
                </div>
                {checked && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                )}
              </div>
            );
          })}
        </div>

        {error && <div style={{ fontSize: 12.5, color: '#f87171', marginTop: 10 }}>{error}</div>}

        <button
          className="btn-primary"
          disabled={selectedIds.length === 0 || submitting}
          onClick={handleCreate}
          style={{ width: '100%', justifyContent: 'center', marginTop: 14, flexShrink: 0 }}
        >
          {submitting ? 'Starting…' : isGroup ? `Create group (${selectedIds.length})` : 'Start chat'}
        </button>
      </div>
    </div>,
    document.body
  );
}
